import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  VStack,
  Button,
  HStack,
  Box,
  Spinner,
  IconButton,
  Divider,
  useColorModeValue,
  Grid,
  Text,
  GridItem,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Flex,
} from "@chakra-ui/react";
import { auth, db } from "../firebase/firebase";
import {
  doc,
  getDoc,
  writeBatch,
  collection,
  getDocs,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { ChakraIcon } from "./ChakraIcon";
import { logToSystem } from "../utils/systemLogging";
import { getProjectPermissions } from "../utils/permissions";
import { showToast } from "../utils/toastUtils";

// Types
type Project = { id: string; name: string };
type Channel = { id: string; name: string };

type AssignProjectsModalProps = {
  isOpen: boolean;
  bg: string;
  color: string;
  onClose: () => void;
  userId: string;
  assignedProjects: string[];
  allProjects: Project[];
  refreshUsers: () => void;
  organizationId: string;
  refreshProjects: () => void;
  currentUser: { id?: string; role: string; permissions?: string };
};

const AssignProjectsModal: React.FC<AssignProjectsModalProps> = ({
  isOpen,
  onClose,
  userId,
  bg,
  color,
  assignedProjects,
  allProjects,
  refreshUsers,
  refreshProjects,
  organizationId,
  currentUser,
}) => {
  const projectPerms = React.useMemo(
    () => getProjectPermissions(currentUser),
    [currentUser]
  );
  const { canAssign: canAssignProjects } = projectPerms;
  const [isSaving, setIsSaving] = useState(false);

  // State
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<
    Record<string, string[]>
  >({});
  const [projectChannels, setProjectChannels] = useState<
    Record<string, Channel[]>
  >({});
  const [loadingChannels, setLoadingChannels] = useState<
    Record<string, boolean>
  >({});
  const [expandedProjects, setExpandedProjects] = useState<
    Record<string, boolean>
  >({});

  const projectHoverBg = useColorModeValue("purple.50", "purple.800");

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const channelRefs = useRef<Record<string, HTMLDivElement>>({});
  // at top of component:
  const [lastOpenedProject, setLastOpenedProject] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!isOpen) return;

    // 1️⃣ Reset any cached channel lists
    setProjectChannels({});

    // 2️⃣ Reset your selectedProjects & reload the member data
    setSelectedProjects(assignedProjects || []);
    preloadAssignedData();

    // 3️⃣ Fire off a fresh channel fetch for each project
    //    (or only for selectedProjects if you prefer)
    allProjects.forEach((proj) => {
      fetchChannelsForProject(proj.id);
    });
  }, [isOpen, assignedProjects, allProjects]);

  // ✅ **Preload Assigned Data**
  const preloadAssignedData = async () => {
    try {
      const orgDocRef = doc(db, "organization", organizationId);
      const orgSnapshot = await getDoc(orgDocRef);

      if (orgSnapshot.exists()) {
        const orgData = orgSnapshot.data();
        const userData = orgData.members.find(
          (member: any) => member.id === userId
        );

        if (userData) {
          setSelectedProjects(userData.assignedProjects || []);
          setSelectedChannels(userData.assignedChannels || {});
        }
      }
    } catch (error) {
      console.error("Error preloading assigned data:", error);
    }
  };

  // ✅ **Fetch Channels for a Project**
  const fetchChannelsForProject = async (projectId: string) => {
    if (!organizationId || projectChannels[projectId]) return;

    setLoadingChannels((prev) => ({ ...prev, [projectId]: true }));

    try {
      const channelsSnapshot = await getDocs(
        collection(
          db,
          `organization/${organizationId}/projects/${projectId}/channels`
        )
      );

      const channels = channelsSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name || "Unnamed Channel",
      }));

      setProjectChannels((prev) => ({ ...prev, [projectId]: channels }));

      // Ensure selected channels are properly checked
      if (selectedChannels[projectId]) {
        setSelectedChannels((prev) => ({
          ...prev,
          [projectId]: prev[projectId] || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching channels:", error);
    } finally {
      setLoadingChannels((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  // ✅ **Toggle project selection**
  const handleProjectSelection = (projectId: string) => {
    setSelectedProjects((prevSelected) => {
      if (prevSelected.includes(projectId)) {
        // If project is unchecked, remove associated channels too
        const newChannels = { ...selectedChannels };
        delete newChannels[projectId];
        setSelectedChannels(newChannels);
        return prevSelected.filter((id) => id !== projectId);
      } else {
        fetchChannelsForProject(projectId);
        return [...prevSelected, projectId];
      }
    });
  };

  // ✅ **Toggle channel selection**
  const handleChannelSelection = (projectId: string, channelId: string) => {
    setSelectedChannels((prev) => {
      const prevSelected = prev[projectId] || [];
      const updatedChannels = prevSelected.includes(channelId)
        ? prevSelected.filter((id) => id !== channelId)
        : [...prevSelected, channelId];

      // ✅ If all channels are selected, ensure project stays ticked
      if (updatedChannels.length === projectChannels[projectId]?.length) {
        setSelectedProjects((prev) => Array.from(new Set([...prev, projectId])));
      }

      return { ...prev, [projectId]: updatedChannels };
    });
  };

  // ✅ **Toggle "Select All" Channels**
  const handleSelectAllChannels = (projectId: string) => {
    const allChannels = projectChannels[projectId]?.map((ch) => ch.id) || [];
    setSelectedChannels((prev) => ({
      ...prev,
      [projectId]:
        prev[projectId]?.length === allChannels.length ? [] : allChannels,
    }));

    // ✅ Ensure project is ticked if all channels are selected
    if (allChannels.length > 0) {
      setSelectedProjects((prev) => Array.from(new Set([...prev, projectId])));
    }
  };

  // ✅ **Toggle Channel Visibility**
  const toggleProjectChannels = (projectId: string) => {
    setExpandedProjects((prev) => {
      const isNowOpen = !prev[projectId];

      // ✅ If we’re opening *and* channels aren’t in state yet, fetch them now
      if (isNowOpen && !projectChannels[projectId]) {
        fetchChannelsForProject(projectId);
      }

      // track for scroll logic
      if (isNowOpen) setLastOpenedProject(projectId);

      return { ...prev, [projectId]: isNowOpen };
    });
  };


  useLayoutEffect(() => {
    // nothing to do until we have a project that’s open and loaded
    if (
      !lastOpenedProject ||
      !expandedProjects[lastOpenedProject] ||
      !projectChannels[lastOpenedProject]
    ) {
      return;
    }

    const container = scrollContainerRef.current;
    const cardEl = channelRefs.current[lastOpenedProject];
    if (!container || !cardEl) return;

    const contRect = container.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();

    // if any part of the card is clipped, scroll it into view
    const isClipped =
      cardRect.top < contRect.top || cardRect.bottom > contRect.bottom;

    if (isClipped) {
      cardEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [
    lastOpenedProject,
    expandedProjects,        // re-run when you expand/collapse
    projectChannels,        // re-run when your channels arrive
  ]);


  // ✅ **Save Assignments**

  const handleSaveAssignments = async () => {
    if (!canAssignProjects || isSaving) return;
    setIsSaving(true);
    try {
      const batch = writeBatch(db);
      const orgRef = doc(db, "organization", organizationId);
      const orgSnap = await getDoc(orgRef);
      if (!orgSnap.exists()) throw new Error("Organization not found");
      const orgData = orgSnap.data();
      const member = orgData.members.find((m: any) => m.id === userId) || {};
      const prevProjects: string[] = member.assignedProjects || [];
      const prevChannels: Record<string, string[]> =
        member.assignedChannels || {};

      const addedProjects = selectedProjects.filter(
        (p) => !prevProjects.includes(p)
      );
      const removedProjects = prevProjects.filter(
        (p) => !selectedProjects.includes(p)
      );
      const channelEvents = selectedProjects
        .map((projectId) => {
          const before = prevChannels[projectId] || [];
          const after = selectedChannels[projectId] || [];
          return {
            projectId,
            added: after.filter((c) => !before.includes(c)),
            removed: before.filter((c) => !after.includes(c)),
          };
        })
        .filter(({ added, removed }) => added.length || removed.length);

      const updatedMembers = orgData.members.map((m: any) =>
        m.id === userId
          ? {
            ...m,
            assignedProjects: selectedProjects,
            assignedChannels: selectedChannels,
          }
          : m
      );
      batch.update(orgRef, { members: updatedMembers });
      batch.set(
        doc(db, "users", userId),
        {
          assignedProjects: selectedProjects,
          assignedChannels: selectedChannels,
        },
        { merge: true }
      );

      addedProjects.forEach((projectId) => {
        batch.set(
          doc(db, `organization/${organizationId}/projects/${projectId}`),
          { assignedUsers: arrayUnion(userId) },
          { merge: true }
        );
      });
      removedProjects.forEach((projectId) => {
        batch.set(
          doc(db, `organization/${organizationId}/projects/${projectId}`),
          { assignedUsers: arrayRemove(userId) },
          { merge: true }
        );
        (prevChannels[projectId] || []).forEach((channelId) => {
          batch.set(
            doc(
              db,
              `organization/${organizationId}/projects/${projectId}/channels/${channelId}`
            ),
            { assignedUsers: arrayRemove(userId) },
            { merge: true }
          );
        });
      });

      channelEvents.forEach(({ projectId, added, removed }) => {
        added.forEach((channelId) => {
          batch.set(
            doc(
              db,
              `organization/${organizationId}/projects/${projectId}/channels/${channelId}`
            ),
            { assignedUsers: arrayUnion(userId) },
            { merge: true }
          );
        });
        removed.forEach((channelId) => {
          batch.set(
            doc(
              db,
              `organization/${organizationId}/projects/${projectId}/channels/${channelId}`
            ),
            { assignedUsers: arrayRemove(userId) },
            { merge: true }
          );
        });
      });

      addedProjects.forEach((projectId) => {
        const name =
          allProjects.find((p) => p.id === projectId)?.name || projectId;
        batch.set(
          doc(db, `organization/${organizationId}/notifications/${userId}`),
          {
            messages: arrayUnion({
              message: `You’ve been assigned to project "${name}".`,
              projectId,
              projectName: name,
              assignedBy: currentUser.role,
              timestamp: new Date(),
              read: false,
            }),
          },
          { merge: true }
        );
      });
      channelEvents.forEach(({ projectId, added }) => {
        const name =
          allProjects.find((p) => p.id === projectId)?.name || projectId;

        added.forEach((channelId) => {
          const channelName =
            projectChannels[projectId]?.find((c) => c.id === channelId)?.name ||
            channelId;
          batch.set(
            doc(db, `organization/${organizationId}/notifications/${userId}`),
            {
              messages: arrayUnion({
                message: `You’ve been added to channel "${channelName}" in project "${name}".`,
                projectId,
                projectName: name,
                channelId,
                assignedBy: currentUser.role,
                timestamp: new Date(),
                read: false,
              }),
            },
            { merge: true }
          );
        });
      });

      // ——— Project unassignment notifications ———
      removedProjects.forEach((projectId) => {
        const projectName =
          allProjects.find((p) => p.id === projectId)?.name || projectId;

        batch.set(
          doc(db, `organization/${organizationId}/notifications/${userId}`),
          {
            messages: arrayUnion({
              message: `You’ve been unassigned from project "${projectName}".`,
              projectId,
              projectName,
              assignedBy: currentUser.role,
              timestamp: new Date(),
              read: false,
            }),
          },
          { merge: true }
        );
      });

      // ——— Channel unassignment notifications ———
      channelEvents.forEach(({ projectId, removed }) => {
        const projectName =
          allProjects.find((p) => p.id === projectId)?.name || projectId;

        removed.forEach((channelId) => {
          const channelName =
            projectChannels[projectId]?.find((c) => c.id === channelId)?.name ||
            channelId;

          batch.set(
            doc(db, `organization/${organizationId}/notifications/${userId}`),
            {
              messages: arrayUnion({
                message: `You’ve been removed from channel "${channelName}" in project "${projectName}".`,
                projectId,
                projectName,
                channelId,
                channelName,
                assignedBy: currentUser.role,
                timestamp: new Date(),
                read: false,
              }),
            },
            { merge: true }
          );
        });
      });

      await batch.commit();

      // Log Activity
      try {
        // Fetch Actor Details (Current User)
        let assignedByName = "Admin/Manager";
        let assignedByEmail = "N/A";

        if (auth.currentUser) {
          const userDocRef = doc(db, "users", auth.currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const uData = userDocSnap.data();
            assignedByName = `${uData.firstName} ${uData.lastName}`;
            assignedByEmail = uData.email;
          }
        }

        // Fetch Target User Details (Assignee)
        let targetUserName = userId;
        const targetUserRef = doc(db, "users", userId);
        const targetUserSnap = await getDoc(targetUserRef);
        if (targetUserSnap.exists()) {
          const tData = targetUserSnap.data();
          targetUserName = `${tData.firstName} ${tData.lastName}`;
        }

        // Construct details string
        const detailsParts: string[] = [];
        if (addedProjects.length > 0) {
          const projNames = addedProjects
            .map((id) => allProjects.find((p) => p.id === id)?.name || id)
            .join(", ");
          detailsParts.push(`Assigned projects: ${projNames}`);
        }
        if (removedProjects.length > 0) {
          const projNames = removedProjects
            .map((id) => allProjects.find((p) => p.id === id)?.name || id)
            .join(", ");
          detailsParts.push(`Unassigned projects: ${projNames}`);
        }

        // Channel updates
        channelEvents.forEach(({ projectId, added, removed }) => {
          const project = allProjects.find((p) => p.id === projectId);
          const projName = project ? project.name : projectId;

          if (added.length > 0) {
            const channelNames = added
              .map((cid) => projectChannels[projectId]?.find((c) => c.id === cid)?.name || cid)
              .join(", ");
            detailsParts.push(`Added to channels in ${projName}: ${channelNames}`);
          }
          if (removed.length > 0) {
            const channelNames = removed
              .map((cid) => projectChannels[projectId]?.find((c) => c.id === cid)?.name || cid)
              .join(", ");
            detailsParts.push(`Removed from channels in ${projName}: ${channelNames}`);
          }
        });

        if (detailsParts.length > 0) {
          await logToSystem(organizationId, "users", {
            type: "USER",
            user: assignedByName,
            email: assignedByEmail,
            action: "Updated Assignments",
            details: `Updated assignments for user ${targetUserName}. ${detailsParts.join("; ")}`,
            status: "success",
          });
        }
      } catch (logErr) {
        console.error("Error logging assignment activity:", logErr);
      }

      refreshUsers();
      refreshProjects();
      showToast({
        title: "Assignments Updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (err) {
      console.error(err);
      showToast({
        title: "Save Error",
        description: "Could not persist assignments. Please retry.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedProjects(assignedProjects || []);
      setExpandedProjects({}); // ✅ Reset expanded projects when modal opens
      preloadAssignedData();
    }
  }, [isOpen, assignedProjects, userId]); // ✅ Add `userId` dependency

  const bgGlass = useColorModeValue(
    "rgba(255, 255, 255, 0.9)",
    "rgba(26, 32, 44, 0.9)"
  );
  const cardBg = useColorModeValue("gray.50", "gray.800");
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const textColor = useColorModeValue("gray.900", "gray.200");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered closeOnOverlayClick={false}>
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent
        borderRadius="lg"
        boxShadow="xl"
        bg={bgGlass}
        border="1px solid"
        borderColor={borderColor}
        p={4}
      >
        <ModalHeader
          fontSize="md"
          fontWeight="bold"
          pb={2}
          color={textColor}
          textAlign="center"
        >
          Assign Projects & Channels
        </ModalHeader>
        <ModalCloseButton color={textColor} />

        <ModalBody p={4} color={textColor}>
          <VStack
            ref={scrollContainerRef}
            align="stretch"
            maxH="400px"
            overflowY="auto"
            spacing={3}
          >
            {allProjects.map((project) => {
              // keep a ref to the whole project card
              const projectRef = (el: HTMLDivElement | null) => {
                if (el) channelRefs.current[project.id] = el;
              };

              return (
                <Box
                  key={project.id}
                  ref={projectRef}
                  p={3}
                  borderRadius="md"
                  bg={cardBg}
                  boxShadow="sm"
                  transition="all 0.2s ease-in-out"
                  _hover={{
                    boxShadow: "md",
                    transform: "scale(1.01)",
                  }}
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <HStack justify="space-between">
                    <Checkbox
                      isChecked={selectedProjects.includes(project.id)}
                      onChange={() => handleProjectSelection(project.id)}
                      fontWeight="medium"
                      color={textColor}
                      fontSize="sm"
                      colorScheme="yellow"
                    >
                      {project.name}
                    </Checkbox>

                    {selectedProjects.includes(project.id) && (
                      <IconButton
                        icon={
                          loadingChannels[project.id] ? (
                            <Spinner size="sm" />
                          ) : expandedProjects[project.id] ? (
                            <ChakraIcon as={FaChevronUp} boxSize={4} />
                          ) : (
                            <ChakraIcon as={FaChevronDown} boxSize={4} />
                          )
                        }
                        onClick={() => toggleProjectChannels(project.id)}
                        aria-label="Toggle channels"
                        size="xs"
                        isDisabled={loadingChannels[project.id]}
                        variant="ghost"
                        _hover={{ bg: hoverBg }}
                      />
                    )}
                  </HStack>

                  {expandedProjects[project.id] && (
                    <Box mt={3} p={3} bg={hoverBg} borderRadius="md" boxShadow="md">
                      <Text fontSize="sm" fontWeight="bold" color={textColor} mb={2}>
                        Channels:
                      </Text>

                      {/* loading indicator */}
                      {loadingChannels[project.id] && !projectChannels[project.id] ? (
                        <Flex justify="center" align="center" py={6}>
                          <Spinner size="lg" />
                        </Flex>
                      ) : (
                        <>
                          <Checkbox
                            isChecked={
                              selectedChannels[project.id]?.length ===
                              projectChannels[project.id]?.length
                            }
                            onChange={() => handleSelectAllChannels(project.id)}
                            fontWeight="medium"
                            color={textColor}
                            fontSize="sm"
                            colorScheme="yellow"
                          >
                            Select All
                          </Checkbox>
                          <Divider my={2} />
                          <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                            {projectChannels[project.id]?.map((channel) => (
                              <Checkbox
                                key={channel.id}
                                isChecked={selectedChannels[project.id]?.includes(channel.id)}
                                onChange={() =>
                                  handleChannelSelection(project.id, channel.id)
                                }
                                color={textColor}
                                fontSize="sm"
                                colorScheme="yellow"
                              >
                                {channel.name}
                              </Checkbox>
                            ))}
                          </Grid>
                        </>
                      )}
                    </Box>
                  )}

                </Box>
              );
            })}
          </VStack>
        </ModalBody>

        {/* Floating Save and Cancel Buttons */}
        <ModalFooter borderTopWidth="1px" pt={3} justifyContent="space-between">
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            _hover={{ bg: hoverBg, transform: "scale(1.03)" }}
          >
            Cancel
          </Button>
          {canAssignProjects && (
            <Button
              variant="gold"
              size="sm"
              onClick={handleSaveAssignments}
              isLoading={isSaving}
              loadingText="Saving..."
              isDisabled={isSaving}
            >
              Save
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AssignProjectsModal;
